'use client';

import type { LucideIcon } from 'lucide-react';
import {
    BarChart3,
    Building2,
    ClipboardList,
    Headphones,
    Network,
} from 'lucide-react';
import { clsx } from 'clsx';

export type ProcessTimelineStep = {
    title: string;
    description: string;
    icon?: LucideIcon;
};

export interface ProcessTimelineProps {
    title: string;
    steps: ProcessTimelineStep[];
    className?: string;
    /** When true, only intro + timeline render (parent supplies the visible section heading) */
    hideTitle?: boolean;
}

const DEFAULT_ICONS: LucideIcon[] = [
    ClipboardList,
    Network,
    Building2,
    Headphones,
    BarChart3,
];

/**
 * Vertical timeline + cards for corporate / MICE service detail pages.
 */
export default function ProcessTimeline({
    title,
    steps,
    className,
    hideTitle = false,
}: ProcessTimelineProps) {
    if (!steps.length) return null;

    const headingId = 'process-timeline-heading';

    return (
        <section
            className={clsx('w-full', className)}
            aria-labelledby={hideTitle ? undefined : headingId}
        >
            <div className="max-w-3xl md:max-w-4xl">
                {!hideTitle && (
                    <h2
                        id={headingId}
                        className="text-2xl font-bold tracking-tight text-alsero-dark-gray md:text-3xl"
                    >
                        {title}
                    </h2>
                )}
            </div>

            <ol
                className="mt-10 max-w-3xl md:mt-12 md:max-w-5xl"
                role="list"
                aria-label={hideTitle ? title : undefined}
            >
                {steps.map((step, index) => {
                    const Icon = step.icon ?? DEFAULT_ICONS[index % DEFAULT_ICONS.length];
                    const isLast = index === steps.length - 1;

                    return (
                        <li key={`${step.title}-${index}`} className="flex gap-4 md:gap-8">
                            {/* Timeline rail */}
                            <div
                                className="flex w-10 shrink-0 flex-col items-center pt-1 md:w-12 md:pt-1.5"
                                aria-hidden
                            >
                                <span
                                    className={clsx(
                                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full md:h-5 md:w-5',
                                        'border-2 border-white bg-alsero-first shadow-md',
                                        'ring-4 ring-alsero-first/20'
                                    )}
                                />
                                {!isLast && (
                                    <div className="mt-2 min-h-[3rem] w-px flex-1 bg-gradient-to-b from-stone-300/90 via-stone-200/80 to-stone-200/30 md:min-h-[4.5rem]" />
                                )}
                            </div>

                            {/* Card */}
                            <article
                                className={clsx(
                                    'mb-8 min-w-0 flex-1 rounded-2xl border border-stone-200/80 bg-white p-6 shadow-sm',
                                    'transition-all duration-300 ease-out',
                                    'hover:border-alsero-first/25 hover:shadow-md md:mb-10 md:p-7',
                                    isLast && 'mb-0 md:mb-0'
                                )}
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                                    <div
                                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-stone-50 text-alsero-first ring-1 ring-stone-200/70"
                                        aria-hidden
                                    >
                                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                                    </div>
                                    <div className="min-w-0 flex-1 space-y-2 text-left">
                                        <h3 className="text-lg font-semibold leading-snug tracking-tight text-alsero-dark-gray md:text-xl">
                                            {step.title}
                                        </h3>
                                        <p className="max-w-prose text-sm leading-relaxed text-alsero-text-tour md:text-[0.9375rem]">
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            </article>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

